import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { injectIntl } from 'react-intl';
// Cloud React components
import { Button, Input, Form, Select, ToggleSwitch } from 'Components';
import Modal, { ModalBody, ModalFooter } from '../../../../../../../components/Modal';
import { NotificationManager } from 'Components/Notifications';
import { Grid, Chip, Icon } from '@mui/material';
import { LandmarkCircleRadiusValues, GeofenceRadiusValues, TypeLayer, TypeObjectMap } from 'Components/Map/providers/enums';
import IconPicker from '../../../../../../../modules/.common/components/IconPicker';
import FromToMap from 'Components/Map/components/FromToMap';
import { useGeocoder } from 'Core/hooks';
import MapObject from 'Components/Map/providers/mapObject';
import { OrderData } from 'Core/data/Helpers';
import { radiusFeetToMeters, firstColorFlagTags, findColor, settingsDesignPolygon } from 'Components/Map/utils'
import Pill from "Components/Pill";

import TagsManage from 'Modules/.common/components/TagsManage';
import InputRadius from 'Components/Map/components/InputRadius';

//actions
import {
    clearRedux,
    mapLandmarksOpenEditCreateModal,
    mapLandmarksGetLandmark,
    setLatLng,
    pointersCreated,
    selectCreateClickMapType,
    addPointers,
    mapLandmarksEditCreateLandmark,
    addUpdateLandmark,
    mapGetLandmarkTagsToCreate,
    mapGetLandmarkTags
} from 'Redux/actions';

//define the area in metters to the circle and square objects
const maximunArea = 65535;

const getDefaultFormData = (defaultColor) => ({
    color: defaultColor,
    iconName: `kmls/flagicon/${defaultColor}.png`
});

const radiusFeetToMetersValue = (radius) => (radius ? radius * 0.3048 : 0);

const detectPreferredMeasureFromFeet = (radiusFeet) => {
    const feet = Number(radiusFeet);
    if (!Number.isFinite(feet) || feet <= 0) return 'Feet';

    const quarterMileFeet = 1320;
    const remainder = feet % quarterMileFeet;
    const distanceToQuarterMile = Math.min(remainder, quarterMileFeet - remainder);

    return feet >= quarterMileFeet && distanceToQuarterMile <= 20 ? 'Miles' : 'Feet';
};

const LandmarkCreateEdit = (props) => {
    const dispatch = useDispatch();
    const { intl, mapRef } = props;
    const { messages } = intl;

    const editCreateLandmark = useRef();
    const fromToMapRef = useRef();
    const pendingSubmitRef = useRef(null);

    //declare the objects to shape at geoform on maps
    const TypeGeo = [
        { id: 1, shape: 'polygon', type: TypeObjectMap.Polygon, name: messages['mapLandmarkCreateEditLandmark_polygon'], maxPoints: 50 },
        { id: 2, shape: 'circle', type: TypeObjectMap.Circle, name: messages['mapLandmarkCreateEditLandmark_circle'], maxPoints: 0 },
        { id: 3, shape: 'square', type: TypeObjectMap.Square, name: messages['mapLandmarkCreateEditLandmark_square'], maxPoints: 0 },
    ];

    const { 
        open,
        id,
        editData : isEdit,
        landmark,
        loadingLandmark,
        response,
        loadingResponse } = useSelector((state) => state.realtimeMapLandmarkCreateEditModuleRedux);

    const {
        pointersCreated: newPoint
    } = useSelector((state) => state.realtimeMapRedux);

    const defaultColor = '8677D9';

    //const { landmarkTagsToCreate: landmarkTags, landmarkTagsToCreateLoading: landmarkTagsLoading } = useSelector((state) => state.realtimeMapLandmarkListModuleRedux);

    const [onGetGeocoder, address, setAddress] = useGeocoder();
    const [landmarkType, setLandmarkType] = useState(null);
    const [formData, setFormData] = useState(getDefaultFormData(defaultColor));
    const [group, setGroup] = useState();
    const [tagErrorMessage, setTagErrorMessage] = useState("");
    const [nameErrorMessage, setNameErrorMessage] = useState("");
    const [restart, setRestart] = useState(0);
    const objectOnMap = 'mapObject_01';
    const defaulRadius = 15.24;

    const [landmarkCreated, setLandmarkCreated] = useState(true);
    const [preferredRadiusMeasure, setPreferredRadiusMeasure] = useState('Feet');
    const isExistingLandmarkEdit = Boolean(id || landmark?.id || isEdit?.id);
    const isPrefilledLandmark = Boolean(isEdit && !isEdit?.id);
    const showCreateSetup = !isExistingLandmarkEdit && !isPrefilledLandmark;
    const titleLandmark = landmark || (isEdit?.id ? isEdit : null);

    useEffect(() => {

        dispatch(mapGetLandmarkTagsToCreate())
        if(!isEdit){
            mapRef?.current?.showLayer(TypeLayer.Units, false);
        }
        settingsDesignPolygon(mapRef, true, `#${defaultColor}`, `#${defaultColor}`); //set the default color to landmark

        return () => {
            mapRef?.current?.removeLayer(TypeLayer.SquareCreate);
            mapRef?.current?.removeLayer(TypeLayer.CircleCreate);
            mapRef?.current?.showUnitsByPreferences();
            setFormData(getDefaultFormData(defaultColor));
            setLandmarkType(null);
            dispatch(clearRedux("MAP_LANDMARKS_EDIT_CREATE"));
        }
    }, []);

    useEffect(() => {
        if (id && open) {
            setGroup(null);
            setTagErrorMessage("");
            setNameErrorMessage("");
            setLandmarkType(null);
            setPreferredRadiusMeasure('Feet');
            setFormData(getDefaultFormData(defaultColor));
            dispatch(mapLandmarksGetLandmark(id));
        } else if (open && isEdit) {
            const nextLandmarkType = { ...isEdit.typeGeo };
            const radiusInMeters = radiusFeetToMetersValue(isEdit?.radius);
            setPreferredRadiusMeasure(detectPreferredMeasureFromFeet(isEdit?.radius));
            let colorLand = defaultColor;

            if (isEdit?.tags) {
                setGroup(isEdit?.tags.map((item) => ({ ...item, name: item.label, color: `#${item.color}` })));
                colorLand = isEdit?.color || isEdit?.tags?.[0]?.color || defaultColor;
            }

            setLandmarkType(nextLandmarkType);
            setFormData({
                ...isEdit,
                lat: parseFloat(isEdit?.latitude || 0), 
                lng: parseFloat(isEdit?.longitude || 0), 
                landmarkIcon: isEdit.iconName ? true : false,
                color: isEdit?.color || colorLand,
                iconName: isEdit?.iconName || `kmls/flagicon/${colorLand}.png`,
                radius: radiusInMeters,
                tagsIds: isEdit?.tags
            });

            if (nextLandmarkType?.type === TypeObjectMap.Circle) {
                mapRef?.current?.removeLayer(TypeLayer.CircleCreate);
                onMapClickCircle({ lat: isEdit.latitude, lng: isEdit.longitude }, "landmarkCircle01", radiusInMeters || defaulRadius);
            }
            
        }
    }, [id, open]);

    useEffect(() => {
        if (response && open) {
            if (response.error) {
                NotificationManager.error(
                    response.message || messages['errorMessage'],
                    messages['realtimemaps_landmarks_create'],
                    3000,
                    null,
                    null,
                    'warning',
                );
                dispatch(clearRedux("MAP_LANDMARKS_EDIT_CREATE", { response: null }));
            } else {
                NotificationManager.success(
                    messages['mapLandmarkCreateEditLandmark_success'],
                    messages['success'],
                    3000,
                    null,
                    null,
                    'success',
                );
                const newLandmark = { ...response, tagsId: response.tags ? response.tags.map((item) => item.id).join(',') : '' }
                dispatch(addUpdateLandmark(landmark ? "update" : "add", newLandmark));
                dispatch(mapGetLandmarkTags())
                handleClose();
            }
        }
    }, [response, open]);

    useEffect(() => {
        if (landmark && open) {
            if (landmark.error) {
                NotificationManager.error(
                    messages['errorMessage'],
                    messages['realtimemaps_landmarks_create'],
                    3000,
                    null,
                    null,
                    'warning',
                );
            } else {
                const nextLandmarkType = TypeGeo.find((item) => item.shape == landmark?.shape);
                const radiusInMeters = radiusFeetToMetersValue(landmark?.radius);
                setPreferredRadiusMeasure(detectPreferredMeasureFromFeet(landmark?.radius));
                setLandmarkType(nextLandmarkType);
                let colorLand = defaultColor;
                if (landmark?.tags) {
                    setGroup(landmark?.tags.map((item) => ({ ...item, name: item.label, color: `#${item.color}` })));
                    colorLand = landmark?.color || landmark.tags[0]?.color || defaultColor;
                }
                setFormData({
                    ...landmark,
                    landmarkIcon: landmark.iconName ? true : false,
                    color: landmark?.color || colorLand,
                    iconName: landmark?.iconName || `kmls/flagicon/${colorLand}.png`,
                    radius: radiusInMeters,
                    tagsIds: landmark?.tags
                });

                if (nextLandmarkType?.type === TypeObjectMap.Circle) {
                    mapRef?.current?.removeLayer(TypeLayer.CircleCreate);
                    onMapClickCircle({ lat: landmark.latitude, lng: landmark.longitude }, "landmarkCircle01", radiusInMeters || defaulRadius);
                }
            }
        }
    }, [landmark, open]);

    useEffect(() => {
        if (address && address.result == true && open) {
            const { data } = address;
            const pendingPayload = pendingSubmitRef.current || formData;
            if (data && data.length > 0) {
                dispatch(mapLandmarksEditCreateLandmark({
                    ...pendingPayload,
                    address: data[0].fulladdress,
                    city: data[0].city,
                    zip: data[0].postalcode,
                    state: data[0].state,
                    country: data[0].country
                }));
                pendingSubmitRef.current = null;
            }
        }else if(open && address){
            const pendingPayload = pendingSubmitRef.current || formData;
            dispatch(mapLandmarksEditCreateLandmark({
                ...pendingPayload,
                address: "",
                city: "",
                zip: "",
                state: "",
                country: ""
            }));
            pendingSubmitRef.current = null;
        }
    }, [address, open]);

    const getDirection = useCallback((lat, lng) => {
        onGetGeocoder({
            from: 'latlng', //latlng, address
            lat: lat,
            lng: lng
        })
    });

    const onGetPoint = useCallback((position, idFromTo) => {
        if((landmarkType?.type === TypeObjectMap.Circle) || isEdit){
            mapRef?.current?.removeLayer(TypeLayer.CircleCreate);
            if (position && position.length > 0) {
                onMapClickCircle({ lat: position[0]?.lat, lng: position[0]?.lng }, idFromTo, formData.radius || defaulRadius);
            }

        } else if(landmarkType?.type === TypeObjectMap.Square){
            mapRef?.current?.removeLayer(TypeLayer.SquareCreate);
            if (position && position.length > 0) {
                onMapClickSquare({ lat: position[0]?.lat, lng: position[0]?.lng }, idFromTo, objectOnMap);
            }
        }
    });

    const onMapClickCircle = useCallback((latLng, idFromTo, radius) => {
        const position = { lat: latLng.lat, lng: latLng.lng };
        mapRef.current.addCircleOnMapToCreate(position, onUpdateList, idFromTo, objectOnMap, radius, maximunArea);
        dispatch(pointersCreated(true));
    });

    const onUpdateList = (distanceInMetters) => {
        setFormData(formData => ({ ...formData, radius: Math.round(distanceInMetters) }))
    }

    const onMapClickSquare = useCallback((latLng, idFromTo, radiusObject) => {

        //testing the object before the add the square
        const objectExist = mapRef.current.getObject(TypeObjectMap.Square, radiusObject) || null
        if (objectExist) return

        mapRef.current.addSquareOnMapToCreate(latLng, idFromTo, radiusObject, formData?.radius || defaulRadius, formData?.freeRectangle, onUpdateList, maximunArea / 2);
        dispatch(pointersCreated(true));
    })

    const onChangeRadius = useCallback((radius) => {
        setFormData((currentFormData) => ({ ...currentFormData, radius: radius }));
        if(landmarkType.type === TypeObjectMap.Circle){
            mapRef?.current?.updateCirclePoint(objectOnMap, null, radius, true);
        }else{
            //update the square object for Diagonally distance
            mapRef.current.setUpdateSquareDiagonally(radius);
        }
    });

    const handleClose = useCallback((e) => {
        if (e) e.preventDefault();

        pendingSubmitRef.current = null;
        setAddress(null);
        setGroup(null);
        setTagErrorMessage("");
        setNameErrorMessage("");
        setPreferredRadiusMeasure('Feet');
        setFormData(getDefaultFormData(defaultColor));
        settingsDesignPolygon(mapRef, true, `#${defaultColor}`, `#${defaultColor}`); //set the default color to landmark
        //setLandmarkType({ id: TypeObjectMap.Polygon, name: messages['mapLandmarkCreateEditLandmark_polygon'], maxPoints: 50 });
        setLandmarkType(null);
        dispatch(mapLandmarksOpenEditCreateModal(false));
        if(isEdit){
            mapRef?.current?.removeLayer(TypeLayer.CircleCreate);
            mapRef?.current?.removeLayer(TypeLayer.SquareCreate);
        }else{
            dispatch(selectCreateClickMapType())
            dispatch(addPointers([]));
            dispatch(setLatLng(null));
            dispatch(clearRedux("MAP_LANDMARKS_EDIT_CREATE"));
        }
    });

    const onSubmit = useCallback(() => {
        if (!landmarkType) {
            return;
        }

        let isValid = true, oneMessage = false;

        if(!formData?.name || formData?.name?.length == 0){
            setNameErrorMessage(messages['required_field'])
            isValid = false;
        }

        if ((landmarkType.type === TypeObjectMap.Circle || landmarkType.type === TypeObjectMap.Square) && !newPoint && !landmark && !oneMessage) {
            NotificationManager.warning(
                messages['mapLandmarkCreateEditLandmark_circleRequired'],
                messages['realtimemaps_landmarks_create'],
                3000,
                null,
                null,
                'success',
            );
            isValid = false;
            oneMessage = true;
        }

        if (!group || (group && group.length == 0)) {
            setTagErrorMessage(messages['required_field'])
            isValid = false;
        }

        let latLng = {};
        let coordinates = [];
        if (landmark) {
            latLng = {
                latitude: landmark.latitude,
                longitude: landmark.longitude
            };
            coordinates = landmark.coordinates.map(coordinate => {
                return { latitude: coordinate.latitude, longitude: coordinate.longitude };
            });
        }

        let shape = "";
        if (landmarkType?.type === TypeObjectMap.Circle) {
            let ccoordinates = mapRef?.current?.getCircleCoordinates();
            setLandmarkCreated(ccoordinates ? true : false)
            if (ccoordinates) {
                coordinates = ccoordinates;
                latLng = {
                    latitude: coordinates[0].latitude,
                    longitude: coordinates[0].longitude
                };
            }

            shape = "circle";
        } else if (landmarkType?.type === TypeObjectMap.Polygon) {
            const { coordinates: pcoordinates, center } = mapRef.current.getPolygonCoordinates();
            setLandmarkCreated(pcoordinates ? true : false)
            if (pcoordinates && pcoordinates.length) {
                coordinates = pcoordinates;
                latLng = center;
            }

            shape = "polygon";
            if (coordinates.length <= 3 && !oneMessage) { //minimun points to polygon is 4
                NotificationManager.warning(
                    messages['mapLandmarkCreateEditLandmark_info_polygon'].replace('{maxPoints}', '50'),
                    messages['realtimemaps_landmarks_create'],
                    3000,
                    null,
                    null,
                    'success',
                );
                isValid = false;
                oneMessage = true;
            }
        } else if (landmarkType?.type === TypeObjectMap.Square) {
            const objectSquare = mapRef.current.getObject(TypeObjectMap.Square, objectOnMap);
            setLandmarkCreated(objectSquare ? true : false)
            //restar the message to normal state after that 8 seconds
            setTimeout(() => { setLandmarkCreated(true)}, 8000)
            if(!objectSquare) return 
            coordinates = objectSquare.coordinates
            shape = "polygon"; //
            latLng = {
                latitude: objectSquare.position.lat,
                longitude: objectSquare.position.lng
            };
        }

        if ((!latLng?.latitude || !latLng.longitude) && !oneMessage) {
            NotificationManager.warning(
                messages['realtimemaps_latLngError'],
                messages['realtimemaps_landmarks_create'],
                3000,
                null,
                null,
                'success',
            );
            isValid = false;
        }

        if(!isValid) return;

        pendingSubmitRef.current = {
            ...formData,
            tagsIds: group.map((item) => item.id),
            //convert metters to feet before the save data
            radius: landmarkType?.type === TypeObjectMap.Circle ? Math.round(formData?.radius * 3.28084) || Math.round(defaulRadius * 3.28084) : 0,
            iconName: formData?.iconName || "",
            latitude: latLng.latitude,
            longitude: latLng.longitude,
            shape: shape,
            coordinates: coordinates
        };

        getDirection(latLng.latitude, latLng.longitude);
        dispatch(clearRedux("MAP_LANDMARKS_EDIT_CREATE", { loadingResponse: true }));
    });

    const pillItemClick = (item) => {
        setLandmarkType(item);
        dispatch(selectCreateClickMapType(item.type, item.maxPoints));
        dispatch(addPointers([]));
        setAddress(null);
        setGroup(null);
        dispatch(setLatLng(null));
        dispatch(pointersCreated(false));
        setFormData({ ...formData, radiusObj: null, radius: null });

        if (fromToMapRef?.current?.clearInputSearchMap) {
            fromToMapRef?.current?.clearInputSearchMap();
        }
    }

    const title = isExistingLandmarkEdit
        ? `${messages['mapLandmarkCreateEditLandmark_editTitle']}${titleLandmark?.name ? ` '${titleLandmark.name}'` : ''}`
        : `${messages['mapLandmarkCreateEditLandmark_createTitle']}`;

    return (
        <Modal
            title={title}
            open={open}
            loading={loadingLandmark || loadingResponse}
            position="right"
            size="small"
            className="modal-top-0"
            headerStyle={{
                top: 0
            }}
            handleClose={handleClose}
            disableBackdropClick={true}
        >
            <ModalBody>
                <div style={{ padding: '18px 24px 24px 24px' }} onMouseDown={(e) => e.stopPropagation()}>
                    <Form ref={editCreateLandmark} onValidSubmit={onSubmit}>
                        {
                            showCreateSetup &&
                            <div style={{ marginBottom: 12 }}>
                                <Grid>
                                    <label className="input-normal-label">{messages["mapLandmarkCreateEditLandmark_tags_shape"]}</label>
                                </Grid>
                                <Grid
                                    container
                                    direction="row"
                                    justifyContent="flex-start"
                                    alignItems="center"
                                    spacing={1}
                                    style={{ marginTop: 2, marginBottom: 10 }}
                                >

                                    {
                                        TypeGeo.map((item) => {
                                            return (
                                                <Grid key={item.id} item xs>
                                                    <Pill
                                                        key={item.id}
                                                        textField={'name'}
                                                        item={item}
                                                        outlined={item.id !== landmarkType?.id}
                                                        onClick={(item) => {
                                                            pillItemClick(item);
                                                            setTimeout(()=> {
                                                                setRestart(restart + 1);
                                                            }, 500);

                                                            //clear the error message
                                                            setTagErrorMessage("");
                                                            setNameErrorMessage("");
                                                            setLandmarkCreated(true);

                                                            //remove object on map
                                                            mapRef?.current?.removeLayer(TypeLayer.CircleCreate);
                                                            mapRef?.current?.removeLayer(TypeLayer.SquareCreate);
                                                        }}
                                                        style={{ border: '0', margin: '2px 0', width: '100%' }}
                                                        label="pill-label-text"
                                                        colorField="default"
                                                        colorSelection={'66BB6A'}
                                                    />
                                                </Grid>
                                            )
                                        })
                                    }
                                </Grid>
                            </div>
                        }

                        {
                            landmarkType &&
                            <React.Fragment>
                                <div style={{ width: '100%' }}>
                                    <label className="input-normal-label">
                                        {messages["mapLandmarkCreateEditLandmark_landmarkIcon"]}
                                    </label>
                                    <div style={{display: 'flex'}}>
                                        <div className="form-item" style={{display: 'inline'}}>
                                            <IconPicker
                                                defaultIcon
                                                restartIcon={formData.restartIcon}
                                                color={formData.color}
                                                icon={formData.iconName || `kmls/flagicon/${formData.color}.png`}
                                                options={["color", "icon"]}
                                                onChange={(color, icon) => {
                                                    //setFormData({ ...formData, color: color, iconName: icon, selectedColor: true })
                                                    setFormData(formData => ({ ...formData, color: color, iconName: icon, selectedColor: true }))
                                                    //define default value for the colors 
                                                    settingsDesignPolygon(mapRef, true, `#${color}`, `#${color}`);
                                                    mapRef.current.updateStyleToCreateObject()
                                                }}
                                            />
                                        </div>
                                        {!formData.iconName?.includes("flagicon") &&
                                            <div style={{display: 'inline', whiteSpace: 'nowrap', marginTop: '20px'}}>
                                                <Button
                                                    className="btn-blue btn-sm"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        setFormData(formData => ({ ...formData, restartIcon: (formData?.restartIcon || 0) +1, iconName: `kmls/flagicon/${formData.color}.png` }))
                                                    }}
                                                >
                                                    {messages['mapIconPicker_defaultIcon']}
                                                </Button>
                                            </div>
                                        }
                                    </div>
                                </div>

                                <div style={{ width: '100%', marginTop: 8 }}>
                                    <label className="input-normal-label">
                                        {messages["mapLandmarkCreateEditLandmark_tags_label"]}
                                        <span className="text-danger"> *</span>
                                    </label>
                                    <Input
                                        id="name"
                                        //type={landmark ? "inline" : "text"}
                                        type={"text"}
                                        placeholder={messages['mapLandmarkCreateEditLandmark_name']}
                                        value={formData?.name || ""}
                                        onChange={(value) => {
                                            setFormData({ ...formData, name: value })
                                            if(value?.length > 0){
                                                setNameErrorMessage("")
                                            }
                                        }}
                                        //required
                                        //requiredMessage={messages['required_field']}
                                        maxLength={50}
                                    />
                                </div>
                                {
                                    nameErrorMessage
                                    &&
                                    <Grid>
                                        <label className="labelError">
                                            {nameErrorMessage}
                                        </label>
                                    </Grid>
                                }

                                <div style={{ marginBottom: 14, marginTop: 10 }}>
                               
                                     <TagsManage
                                        onlyTags={true}
                                        mode="selector"
                                        enableSelectAll={false}
                                        disableUntaggedSelection={true}
                                        label={messages["unit_settings_tags_simple"]}
                                        placeholder={messages["unit_settings_tags_search"]}
                                        value={group || []}
                                        required
                                        onChange={(values) => {
                                            let newValues = [...values]
                                            //Add the number off the vehicles when user select a new element
                                            if (group && newValues && newValues.length > 0 && group.length < newValues.length) {
                                                newValues[newValues.length - 1] = {
                                                    ...newValues[newValues.length - 1],
                                                }
                                            }

                                            if (!formData?.selectedColor) {
                                                const colorFlag = firstColorFlagTags(newValues, formData?.color || defaultColor, formData?.selectedColor);
                                                setFormData({
                                                    ...formData,
                                                    color: colorFlag,
                                                    iconName: `kmls/flagicon/${colorFlag}.png`
                                                });
                                            }

                                            setGroup(newValues)
                                            setTagErrorMessage("")
                                            //setFormData({ ...formData, tags: newValues });
                                        }}
                                        menuStyle={{ maxHeight: '280px', maxWidth: '417px' }}
                                        isVisiblePill={true}
                                    />
                                    {
                                        tagErrorMessage && <small className='text-danger'>{tagErrorMessage}</small>
                                    }
                                </div>

                                {
                                    showCreateSetup ?
                                        <div style={{ marginBottom: 10 }}>
                                            <Grid>
                                                <label className="input-normal-label">{messages["unit_settings_tags_type"]}</label>
                                            </Grid>
                                            {
                                                (landmarkType?.type === TypeObjectMap.Circle || landmarkType?.type === TypeObjectMap.Square) ?
                                                    <FromToMap
                                                        id={"landmarkCircle01"}
                                                        ref={fromToMapRef}
                                                        noMapClick
                                                        onGetAddress={(event) => {
                                                            onGetPoint(event, "landmarkCircle01");
                                                        }}
                                                        onGetPosition={(position, id) => {
                                                            onGetPoint([position], id);
                                                        }}
                                                        onSelectSelectUnit={(unit) => {
                                                            onGetPoint([{
                                                                ...unit?.lastReading,
                                                                lat: unit?.lat,
                                                                lng: unit?.lng
                                                            }], 'landmarkCircle01');
                                                        }}
                                                        onClear={() => {
                                                            mapRef?.current?.removeLayer(TypeLayer.CircleCreate);
                                                            mapRef?.current?.removeLayer(TypeLayer.SquareCreate);
                                                        }}
                                                        onClearMarker={() => {
                                                            mapRef.current.removeMapObject(objectOnMap, TypeObjectMap.Circle);
                                                            mapRef.current.removeMapObject("landmarkCircle01", TypeObjectMap.Marker);
                                                        }}
                                                        messages={messages}
                                                        required={true}
                                                        getCoordinates
                                                        pills
                                                        restart={restart}
                                                        maxPoints={landmarkType.maxPoints}
                                                        entities={["ADDRESS", "MAP_CLICK", "UNITS", "LATLNG"]}
                                                    /> : 
                                                    <FromToMap
                                                        id={"landmarkPolygon01"}
                                                        ref={fromToMapRef}
                                                        onSelectSelectUnit={(unit) => {
                                                            mapRef?.current?.centerPointOnMap(unit?.lat, unit?.lng, 11);
                                                        }}
                                                        messages={messages}
                                                        createElements
                                                        required={false}
                                                        noMapClick
                                                        pills
                                                        isPolygon
                                                        restart={restart}
                                                        maxPoints={landmarkType.maxPoints}
                                                        entities={["ADDRESS", "MAP_CLICK", "UNITS"]}
                                                    />
                                            }
                                            {
                                                !landmarkCreated
                                                &&
                                                <Grid>
                                                    <label className="labelError">
                                                        {messages["mapLandmarkCreateEditLandmark_error_define_landmark"]}
                                                    </label>
                                                </Grid>
                                            }
                                        </div> :
                                        <div>
                                            <div>
                                                <Input
                                                    id="name"
                                                    type="text"
                                                    label={messages['mapLandmarkCreateEditLandmark_address']}
                                                    placeholder={messages['mapLandmarkCreateEditLandmark_address']}
                                                    value={formData.address}
                                                    onChange={(value) => { }}
                                                    disabled
                                                />
                                            </div>

                                            <div>
                                                <Input
                                                    id="name"
                                                    type="text"
                                                    label={messages['mapLandmarkCreateEditLandmark_latLng']}
                                                    placeholder={messages['mapLandmarkCreateEditLandmark_latLng']}
                                                    value={`${parseFloat(formData.latitude || 0).toFixed(6)},${parseFloat(formData.longitude || 0).toFixed(6)}`}
                                                    onChange={(value) => { }}
                                                    //disabled
                                                    readOnly
                                                />
                                            </div>
                                        </div>
                                }

                                {
                                    ((landmarkType?.type === TypeObjectMap.Circle) || landmarkType?.type === TypeObjectMap.Square) &&
                                    <div>
                                        <Grid>
                                            <InputRadius 
                                                value={formData?.radius ?? defaulRadius}
                                                onChange={onChangeRadius}
                                                options={GeofenceRadiusValues}
                                                preferredMeasure={preferredRadiusMeasure}
                                                isSquare={(landmarkType?.type === TypeObjectMap.Square) || false}
                                            >
                                            </InputRadius>
                                        </Grid>
                                    </div>
                                }

                                <div style={{ marginBottom: 10 }}>
                                    <Input
                                        id="note"
                                        type="textarea"
                                        label={messages['mapLandmarkCreateEditLandmark_note']}
                                        placeholder={messages['mapLandmarkCreateEditLandmark_note']}
                                        value={formData.note || ""}
                                        onChange={(value) => setFormData({ ...formData, note: value })}
                                        maxLength={65000} //DB TEXT type
                                    />
                                </div>
                            </React.Fragment>
                        }
                    </Form>
                </div>
            </ModalBody>

            <ModalFooter style={{ bottom: 50 }}>
                <Button
                    disabled={loadingLandmark || loadingResponse}
                    onClick={handleClose}
                    className="btn-cancel"
                >
                    {messages['cancel']}
                </Button>

                <Button
                    disabled={loadingLandmark || loadingResponse}
                    onClick={(e) => {
                        if (e) e.preventDefault();
                        editCreateLandmark.current.submit();
                    }}
                    className="btn-blue"
                >
                    {messages['save']}
                </Button>
            </ModalFooter>
        </Modal>
    )
}

export default injectIntl(LandmarkCreateEdit, { withRef: true });
